import { useMemo } from "react";

import {
  getVisibleMetrics,
  getHighlightedMetricNames,
  groupMetrics,
  calculateDerivedMetrics,
  formatOptimizationGoal,
} from "@/services/metricsService";
import { ObjectiveBucket, PerformanceContextResponse } from "@/types/metrics";

import { MetricsAccordion } from "./MetricsAccordion";
import { ScopeToggle } from "./ScopeToggle";

interface PerformanceMetricsPanelProps {
  objective: ObjectiveBucket | null;
  performanceMetrics: Record<string, number | null>;
  isMetaImported?: boolean;
  performanceContext?: PerformanceContextResponse | null;
  onMetricScopeChange?: (scope: "all" | "bucket" | "goal") => void;
  onProjectScopeChange?: (scope: "project" | "team") => void;
  metricScope?: "all" | "bucket" | "goal";
  projectScope?: "project" | "team";
  mediaType?: "video" | "image";
  startDate?: string;
  endDate?: string;
  onDateRangeChange?: (range: { startDate?: string; endDate?: string }) => void;
  peerMediaType?: "video" | "image";
  onPeerMediaTypeChange?: (type: "video" | "image" | undefined) => void;
  onFetchWindowReach?: () => void;
  isFetchingWindowReach?: boolean;
  className?: string;
}

export function PerformanceMetricsPanel({
  objective,
  performanceMetrics,
  isMetaImported,
  performanceContext,
  onMetricScopeChange,
  onProjectScopeChange,
  metricScope,
  projectScope,
  mediaType,
  startDate,
  endDate,
  onDateRangeChange,
  peerMediaType,
  onPeerMediaTypeChange,
  onFetchWindowReach,
  isFetchingWindowReach,
  className,
}: PerformanceMetricsPanelProps) {
  // Calculate derived metrics
  const derivedMetrics = useMemo(
    () => calculateDerivedMetrics(performanceMetrics),
    [performanceMetrics]
  );

  // Merge base + derived metrics
  const allMetrics = useMemo(
    () => ({ ...performanceMetrics, ...derivedMetrics }),
    [performanceMetrics, derivedMetrics]
  );

  // When a date filter is active and the backend returned windowed values,
  // override snapshot values with the time-series aggregated values so the
  // displayed numbers match the date window being compared against peers.
  const displayMetrics = useMemo(() => {
    if (performanceContext?.usedDailyAggregation && performanceContext?.percentiles) {
      // Overlay aggregated values onto snapshot; handles Meta-imported creatives
      // whose snapshot may be empty but whose timeseries carries the real values.
      const pctEntries = Object.fromEntries(
        Object.entries(performanceContext.percentiles).map(([key, info]) => [key, info.value])
      );
      return { ...allMetrics, ...pctEntries };
    }
    return allMetrics;
  }, [allMetrics, performanceContext]);

  // Get visible metrics for this objective (excluding video metrics for image creatives)
  const visibleSpecs = useMemo(
    () => getVisibleMetrics(objective, undefined, mediaType),
    [objective, mediaType]
  );

  // Get highlighted metric names
  const highlightedNames = useMemo(
    () => getHighlightedMetricNames(objective),
    [objective]
  );

  // Group metrics by category
  const groupedMetrics = useMemo(
    () => groupMetrics(visibleSpecs, displayMetrics),
    [visibleSpecs, displayMetrics]
  );

  // True when the user has an active date filter but the creative had no activity in that window.
  // In this state we hide the metrics accordion — the snapshot must not bleed through.
  const noActivityInWindow = !!(
    startDate && endDate &&
    performanceContext &&
    !performanceContext.usedDailyAggregation
  );

  if (groupedMetrics.length === 0) {
    return (
      <div className={className}>
        <p className="text-sm text-gray-500">
          No performance metrics available.
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Performance Metrics
        </h3>
        {objective && (
          <p className="text-sm text-gray-500 mt-1">
            Showing metrics for{" "}
            <span className="font-medium">{objective}</span> objective.
            Highlighted metrics are most relevant.
          </p>
        )}
        {!objective && (
          <p className="text-sm text-gray-500 mt-1">
            No objective set. Showing universal metrics only.
          </p>
        )}
      </div>

      {/* Scope Toggle (only for Meta-imported creatives) */}
      {isMetaImported && performanceContext?.scope && (
        <ScopeToggle
          availableMetricScopes={performanceContext.scope.availableMetricScopes}
          metricScope={metricScope || performanceContext.scope.metricScope}
          projectScope={projectScope || performanceContext.scope.projectScope}
          onMetricScopeChange={onMetricScopeChange || (() => {})}
          onProjectScopeChange={onProjectScopeChange || (() => {})}
          objectiveBucket={performanceContext.scope.objectiveBucket}
          optimizationGoalLabel={performanceContext.scope.optimizationGoal ? formatOptimizationGoal(performanceContext.scope.optimizationGoal) : null}
          sampleSize={performanceContext.sampleSize}
          warnings={performanceContext.warnings}
          startDate={startDate}
          endDate={endDate}
          onDateRangeChange={onDateRangeChange || (() => {})}
          peerMediaType={peerMediaType}
          onPeerMediaTypeChange={onPeerMediaTypeChange || (() => {})}
          className="mb-4"
        />
      )}

      {/* Empty state: date filter active but no ad activity in this window */}
      {noActivityInWindow ? (
        <div className="py-6 text-center">
          <p className="text-sm font-medium text-gray-700">No ad activity in this date range</p>
          <p className="mt-1 text-xs text-gray-400">{startDate} → {endDate}</p>
        </div>
      ) : (
        <>
          {/* On-demand reach fetch: shown when date filter is active, daily aggregation is
              used, and reach is absent from the window aggregate (not yet fetched for this range) */}
          {startDate && endDate &&
            performanceContext?.usedDailyAggregation &&
            displayMetrics["reach"] == null &&
            onFetchWindowReach && (
            <div className="mb-3 flex items-center gap-2 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-500">
              <span>Reach for this date range requires a Meta fetch.</span>
              <button
                onClick={onFetchWindowReach}
                disabled={isFetchingWindowReach}
                className="flex items-center gap-1 font-medium text-blue-500 hover:text-blue-700 disabled:opacity-50"
                title="Fetch deduplicated reach from Meta for this exact date range (cached after first fetch)"
              >
                {isFetchingWindowReach ? "Fetching…" : "↻ Fetch reach"}
              </button>
            </div>
          )}
          <MetricsAccordion
            groups={groupedMetrics}
            highlightedMetrics={highlightedNames}
            percentileData={isMetaImported ? performanceContext?.percentiles : undefined}
          />
        </>
      )}
    </div>
  );
}
